<template>
	<v-container
		id="login"
		class="fill-height justify-center"
		tag="section"
		v-if="loginMethods && selectedLoginMethod"
	>
		<v-row justify="center">
			<v-slide-y-transition appear>
				<base-material-card
					class="px-5 py-3"
					color="success"
					light
					max-width="100%"
					width="400"
				>
					<template v-slot:heading>
						<div class="text-center">
							<span class="text-h2 mb-2"> IRIS </span>
							<span class="text-h2 font-weight-thin"
								>Asset Web Portal</span
							>
						</div>
						<div class="text-center">
							<span class="text font-weight-thin">
								{{ applicationVersion }}
							</span>
						</div>
					</template>

					<v-card-text class="text-center">
						<div
							class="text-center grey--text text-body-1 font-weight-light"
						>
							Sign in to start your session
						</div>

						<v-form
							ref="form_FormsLogin"
							v-if="selectedLoginMethod === irisLoginMethod.Forms"
						>
							<v-select
								label="Login Method"
								:items="loginMethods"
								v-model="selectedLoginMethod"
								:rules="[rules.required]"
							>
								<template v-slot:selection="{ item }">
									{{ getLoginMethod(item) }}
								</template>
								<template v-slot:item="{ item }">
									{{ getLoginMethod(item) }}
								</template>
							</v-select>

							<v-text-field
								ref="Email"
								v-model="Email"
								:rules="[rules.required, rules.email]"
								color="secondary"
								label="Email..."
								prepend-icon="mdi-email"
							/>

							<v-text-field
								ref="Password"
								v-model="Password"
								:append-icon="show ? 'mdi-eye' : 'mdi-eye-off'"
								:rules="[rules.required]"
								:type="show ? 'text' : 'password'"
								class="mb-4"
								color="secondary"
								label="Password..."
								prepend-icon="mdi-lock-outline"
								@click:append="togglePassVisibility"
								@keydown.enter="login"
							/>

							<div
								class="text-center error--text text-body-1 font-weight-light mb-4"
							>
								<span v-if="authError"
									>Invalid Email or Password</span
								>
							</div>
						</v-form>

						<v-form
							ref="form_WindowsLogin"
							v-if="
								selectedLoginMethod === irisLoginMethod.Windows
							"
						>
							<v-select
								label="Login Method"
								:items="loginMethods"
								v-model="selectedLoginMethod"
								:rules="[rules.required]"
							>
								<template v-slot:selection="{ item }">
									{{ getLoginMethod(item) }}
								</template>
								<template v-slot:item="{ item }">
									{{ getLoginMethod(item) }}
								</template>
							</v-select>
							<v-text-field
								disabled
								label="Domain \ Username"
								v-model="windowsUsername"
								:rules="[rules.required]"
							/>
							<div
								class="text-center error--text text-body-1 font-weight-light mb-4"
							>
								<span v-if="authError"
									>Windows Authentication Failed</span
								>
							</div>
						</v-form>

						<pages-btn
							class="v-btn--text success--text"
							color=""
							depressed
							large
							@click.prevent="login"
						>
							Sign in
						</pages-btn>
					</v-card-text>
				</base-material-card>
			</v-slide-y-transition>
		</v-row>
	</v-container>
</template>

<script>
import appVersion from '../utils/appVersionUtils';
import { GeneralActions, AuthActionTypes } from '../store/actions';
import alertHandler from '@/mixins/alertHandler';
import { mapGetters } from 'vuex';
import { AuthGetterTypes } from '../store/getters';
import { IrisLoginMethod } from '../enums/irisLoginMethod';

export default {
	name: 'Login',
	components: {
		PagesBtn: () => import('@/components/Btn'),
		BaseMaterialCard: () => import('@/components/base/MaterialCard'),
	},
	mixins: [alertHandler],
	computed: {
		...mapGetters({
			loginMethods: [AuthGetterTypes.LOGIN_METHODS],
		}),
	},
	data() {
		return {
			irisLoginMethod: IrisLoginMethod,
			selectedLoginMethod: null,
			applicationVersion: appVersion.getVersion(),
			formHasErrors: false,
			show: false,
			ProjectId: '',
			Email: '',
			Password: '',
			windowsUsername: null,
			rules: {
				required: (value) => !!value || 'Field is required',
				email: (value) => {
					const pattern =
						/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
					return pattern.test(value) || 'Invalid email format';
				},
			},
			authError: false,
		};
	},
	watch: {
		selectedLoginMethod(newVal) {
			this.authError = null;
			if (newVal == IrisLoginMethod.Windows) {
				this.$store
					.dispatch(AuthActionTypes.LOAD_WINDOWS_USERNAME)
					.then((resp) => {
						if (resp) {
							this.windowsUsername = resp;
							this.AutoLoginWindows();
						}
					});
			} else {
				this.windowsUsername = null;
			}
		},
	},
	mounted() {
		this.$store.dispatch(GeneralActions.LOADING, false);
		this.$store.dispatch(AuthActionTypes.LOGOUT).then(() => {
			this.$store
				.dispatch(AuthActionTypes.LOAD_LOGIN_METHODS)
				.then((success) => {
					if (!success) return;
					this.selectedLoginMethod = this.loginMethods[0];
				});
		});
	},

	methods: {
		AutoLoginWindows() {
			if (sessionStorage.windowsAutoLogin) return;
			this.login();
			sessionStorage.windowsAutoLogin = true;
		},

		login() {
			if (this.selectedLoginMethod == IrisLoginMethod.Forms) {
				this.LoginForms();
			}

			if (this.selectedLoginMethod == IrisLoginMethod.Windows) {
				this.LoginWindows();
			}
		},

		LoginForms() {
			if (!this.$refs['form_FormsLogin'].validate()) return;
			this.$store.dispatch(GeneralActions.LOADING, true);
			this.$store
				.dispatch(AuthActionTypes.LOGIN, {
					Email: this.Email,
					Password: this.Password,
				})
				.then(() => {
					this.$router.push('/homepage');
				})
				.catch(() => {
					this.authError = true;
				})
				.finally(() => {
					this.$store.dispatch(GeneralActions.LOADING, false);
				});
		},

		LoginWindows() {
			if (!this.$refs['form_WindowsLogin'].validate()) return;

			this.$store.dispatch(GeneralActions.LOADING, true);
			this.$store
				.dispatch(AuthActionTypes.LOGIN_WINDOWS)
				.then(() => {
					this.$router.push('/homepage');
				})
				.catch(() => {
					this.authError = true;
				})
				.finally(() => {
					this.$store.dispatch(GeneralActions.LOADING, false);
				});
		},

		togglePassVisibility() {
			this.show = !this.show;
		},
		getLoginMethod(value) {
			switch (value) {
				case IrisLoginMethod.Forms:
					return 'Forms';
				case IrisLoginMethod.Windows:
					return 'Windows';
				case IrisLoginMethod.SingleSignOn:
					return 'Single Sing On';
				default:
					return 'Unknown';
			}
		},
	},
};
</script>
